import React, { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Layout from './layout';
import { auth } from './apiClient';
import LoadingSpinner from './components/ui/LoadingSpinner';
import ToastManager from './components/ui/ToastManager';
import { setGlobalToastRef } from './hooks/useAppToast';
import { hasExtendedSession, getAuthData, clearAuthData } from './utils/authUtils';

// Ленивая загрузка страниц
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Projects = lazy(() => import('./pages/Projects'));
const Inspections = lazy(() => import('./pages/Inspections'));
const Defects = lazy(() => import('./pages/Defects'));
const UsersPage = lazy(() => import('./pages/Users'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const Events = lazy(() => import('./pages/Events'));
const InspectionRequests = lazy(() => import('./pages/InspectionRequests'));
const Documentation = lazy(() => import('./pages/Documentation'));
const AllRemarks = lazy(() => import('./pages/AllRemarks'));
const Correspondence = lazy(() => import('./pages/Correspondence'));
const CorrespondenceDetails = lazy(() => import('./pages/CorrespondenceDetails'));
const CorrespondentsPage = lazy(() => import('./pages/CorrespondentsPage'));
const ProjectGpr = lazy(() => import('./pages/ProjectGpr'));

// Ленивая загрузка детальных компонентов
const ProjectDetails = lazy(() => import('./components/projects/ProjectDetails'));
const InspectionDetails = lazy(() => import('./components/inspections/InspectionDetails'));
const DefectDetails = lazy(() => import('./components/defects/DefectDetails'));
const RemarkDetails = lazy(() => import('./components/documentation/RemarkDetails'));


// Компонент-обертка для защищенных маршрутов
function PrivateRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        return;
      }
      try {
        await auth.me(); // Проверяем, валиден ли токен
        setIsAuthenticated(true);
      } catch (error) {
        clearAuthData(); // Очищаем все данные авторизации
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  if (isAuthenticated === null) {
    return <div>Загрузка...</div>; // Или спиннер
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

export default function App() {
  const navigate = useNavigate();
  const toastRef = useRef();
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Устанавливаем глобальный ref при монтировании
  useEffect(() => {
    setGlobalToastRef(toastRef);
    return () => setGlobalToastRef(null);
  }, []);

  // Отслеживаем активность пользователя для расширенных сессий
  useEffect(() => {
    const updateActivity = () => {
      setLastActivity(Date.now());
    };

    // События, которые указывают на активность пользователя
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, []);

  // Проверяем и продлеваем сессию при необходимости
  useEffect(() => {
    const checkSession = () => {
      const { token, expiresIn, rememberMe } = getAuthData();
      
      if (!token || !rememberMe) return;

      // Проверяем, не истекла ли сессия
      const tokenAge = Date.now() - lastActivity;
      const maxInactivity = 14 * 24 * 60 * 60 * 1000; // 14 дней в миллисекундах
      
      if (tokenAge > maxInactivity) {
        // Сессия истекла due to inactivity
        clearAuthData();
        window.location.href = '/login';
        return;
      }

      // Обновляем активность каждый час при активной сессии
      const activityInterval = setInterval(() => {
        if (Date.now() - lastActivity < 60 * 60 * 1000) { // Активность в последний час
          // Пользователь активен, обновляем timestamp
          localStorage.setItem('lastActivity', Date.now());
        }
      }, 60 * 60 * 1000); // Каждый час

      return () => clearInterval(activityInterval);
    };

    const interval = checkSession();
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [lastActivity]);

  const handleLogin = (redirectTo) => {
    navigate(redirectTo || '/');
  };

  return (
    <ToastManager ref={toastRef}>
      <Routes>
        <Route path="/login" element={
          <Suspense fallback={<LoadingSpinner fullScreen size="large" />}>
            <LoginPage onLogin={handleLogin} />
          </Suspense>
        } />

        {/* Защищенные маршруты */}
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <Layout>
                <Suspense fallback={<LoadingSpinner fullScreen size="large" />}>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/events" element={<Events />} />
                    
                    {/* --- Новая страница заявок --- */}
                    <Route path="/requests" element={<InspectionRequests />} />
                    <Route path="/documentation" element={<Documentation />} />
                    <Route path="/documentation/remarks" element={<AllRemarks />} />
                    <Route path="/documentation/remarks/:remarkId" element={<RemarkDetails />} />

                    <Route path="/projects" element={<Projects />} />
                    <Route path="/projects/:projectId" element={<ProjectDetails />} />

                    <Route path="/inspections" element={<Inspections />} />
                    <Route path="/inspections/:inspectionId" element={<InspectionDetails />} />

                    <Route path="/defects" element={<Defects />} />
                    <Route path="/defects/:defectId" element={<DefectDetails />} />

                    <Route path="/users" element={<UsersPage />} />
                    <Route path="/correspondence" element={<Correspondence />} />
                    <Route path="/correspondence/:id" element={<CorrespondenceDetails />} />
                    <Route path="/correspondents" element={<CorrespondentsPage />} />
                    <Route path="/projects/:projectId/gpr" element={<ProjectGpr />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </Layout>
            </PrivateRoute>
          }
        />
      </Routes>
    </ToastManager>
  );
}